import { BrowserAgent } from '@newrelic/browser-agent/loaders/browser-agent';
import { ENV } from '@/config/env';

export type TelemetryUser = {
  id: string | number;
  tenantId: string | null;
  role: string | null;
};

let agent: BrowserAgent | undefined;

export const initTelemetry = (): void => {
  const { licenseKey, applicationId, accountId } = ENV.newRelic;
  if (agent || !licenseKey || !applicationId || !accountId) return;

  agent = new BrowserAgent({
    init: {
      distributed_tracing: {
        enabled: true,
        allowed_origins: [new URL(ENV.apiBaseUrl).origin],
        cors_use_tracecontext_headers: true,
        cors_use_newrelic_header: false,
        exclude_newrelic_header: true,
      },
      // Console levels and replay sampling are server-controlled in agent 1.321.0.
      // Configure WARN logs and replay 0% normal / 100% errors: docs/OBSERVABILITY.md.
      logging: { enabled: true },
      session_replay: { enabled: true, mask_all_inputs: true, mask_text_selector: '*' },
    },
    info: {
      beacon: 'bam.eu01.nr-data.net',
      errorBeacon: 'bam.eu01.nr-data.net',
      licenseKey,
      applicationID: applicationId,
      jsAttributes: {
        'service.name': 'erp-frontend',
        environment: import.meta.env.MODE === 'staging' ? 'staging' : 'production',
        ...(ENV.releaseVersion ? { 'application.version': ENV.releaseVersion } : {}),
      },
    },
    loader_config: {
      accountID: accountId,
      // Standalone Browser app mapping; verify against its generated install snippet.
      trustKey: accountId,
      agentID: applicationId,
      licenseKey,
      applicationID: applicationId,
    },
  });
};

export const captureError = (
  error: Error | string,
  context?: Record<string, string | number | boolean | null>,
): void => {
  agent?.noticeError(error, context);
};

export const tagUser = (user: TelemetryUser | null): void => {
  agent?.setCustomAttribute('tenant.id', user?.tenantId ?? null);
  agent?.setCustomAttribute('user.id', user?.id ?? null);
  agent?.setCustomAttribute('user.role', user?.role ?? null);
};
